// Styles
import "./interface-switcher.scss";
// Tools
import { useDispatch } from "react-redux";
import { useEffect, useState, useCallback } from "react";
// Actions
import {
	resetSectionsOnly,
	showOnlyDashboard,
	showOnlyChat,
	setConfiguration,
	bumpLayoutHardResetNonce,
	startSectionsConfig,
	startModulesConfig,
} from "../../../../../../shared/store/slices/app.slice";
// Services & types
import { getAuthData } from "../../../../../../pages/main/service/auth-me.service";
import { setUserData } from "../../../../../../shared/store/slices/user.slice";
import { Module } from "../../../../../../shared/types/user.type";
import { mergeFreshModulesPreferringStorage } from "../../../../../../shared/utils/merge-fresh-modules-preferring-storage";
import { readStoredModulesConfig } from "../../../../../../shared/utils/read-stored-modules-config";
import { readStoredSectionsConfig } from "../../../../../../shared/utils/read-stored-sections-config";

/* 
	Определяем статус конфигурации модулей и секций
	Возможные статусы:
	 - CHAT -есть только модули из секции Chat
	 - DASHBOARD -есть только модули из секции Dashboard
	 - ALL -есть модули из секций Chat и Dashboard
	 - NONE -нет модулей
*/
const getModulesConfigStatus = (modules: Module[]) => {
	if (modules.length === 0) return "NONE";
	if (modules.every((module) => module.module_category === "chat"))
		return "CHAT";
	if (modules.every((module) => module.module_category === "dashboard"))
		return "DASHBOARD";
	if (
		modules.some((module) => module.module_category === "chat") &&
		modules.some((module) => module.module_category === "dashboard")
	)
		return "ALL";
	return "NONE";
};

export const InterfaceSwitcher = () => {
	const dispatch = useDispatch();

	// Стейт для управления кнопками (NONE, CHAT, DASHBOARD, ALL)
	const [modulesAccessStatus, setModulesAccessStatus] =
		useState<string>("NONE");

	// Синхронизация конфигурации модулей с auth/me + запись в store/LS через setConfiguration
	const syncConfigurationFromAuth = useCallback(
		async (opts: { preserveStoredLayout: boolean }) => {
			const snapSections = opts.preserveStoredLayout
				? readStoredSectionsConfig()
				: null;
			const snapModules = opts.preserveStoredLayout
				? readStoredModulesConfig()
				: null;

			const response = await getAuthData();
			if (!response?.data) return;
			dispatch(setUserData(response.data));

			const modules: Module[] = response.data.modules ?? [];
			setModulesAccessStatus(getModulesConfigStatus(modules));

			const categoryCounters: Record<string, number> = {};
			const freshModulesConfig = modules.map((mod) => {
				const defaults = startModulesConfig.find(
					(d) => d.moduleName === mod.module_name,
				);
				categoryCounters[mod.module_category] =
					(categoryCounters[mod.module_category] ?? 0) + 1;
				return {
					moduleName: mod.module_name,
					moduleCategory: mod.module_category,
					moduleWeight: defaults?.moduleWeight ?? 1,
					moduleOrder:
						defaults?.moduleOrder ?? categoryCounters[mod.module_category],
				};
			});

			const mergedModules = opts.preserveStoredLayout
				? mergeFreshModulesPreferringStorage(freshModulesConfig, snapModules)
				: freshModulesConfig;

			const sectionsConfig = !opts.preserveStoredLayout
				? startSectionsConfig
				: snapSections && snapSections.length > 0
					? snapSections
					: startSectionsConfig;

			dispatch(
				setConfiguration({
					modulesConfig: mergedModules,
					sectionsConfig,
				}),
			);

			if (!opts.preserveStoredLayout) {
				dispatch(bumpLayoutHardResetNonce());
			}
		},
		[dispatch],
	);

	useEffect(() => {
		void syncConfigurationFromAuth({ preserveStoredLayout: true });
	}, [syncConfigurationFromAuth]);

	return (
		<div className='interface-switcher'>
			{modulesAccessStatus !== "NONE" && (
				<div className='interface-switcher__wrapper'>
					<button
						type='button'
						onClick={() =>
							void syncConfigurationFromAuth({
								preserveStoredLayout: false,
							})
						}
						title='Скинути розкладку й повернути приховані панелі'
						className='interface-switcher__button refresh mobile'>
						<img src='/img/icons/refresh.png' alt='reset windows' />
					</button>
					{modulesAccessStatus === "ALL" && (
						<>
							<button
								onClick={() => dispatch(resetSectionsOnly())}
								title='Показати дашборд і чат'
								className='interface-switcher__button desktop'>
								<img src='/img/icons/two-windows.png' alt='split windows' />
							</button>
							<button
								onClick={() => dispatch(showOnlyDashboard())}
								title='Лише дашборд'
								className='interface-switcher__button desktop'>
								<img src='/img/icons/top-window.png' alt='top window' />
							</button>
							<button
								onClick={() => dispatch(showOnlyChat())}
								title='Лише чат'
								className='interface-switcher__button desktop'>
								<img src='/img/icons/bottom-window.png' alt='bottom window' />
							</button>
						</>
					)}
				</div>
			)}
		</div>
	);
};
